'use client';

import { c as _c } from "react/compiler-runtime";
import type { HeroState } from './utils';
import type React from 'react';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import { GaugeContainer, GaugeReferenceArc, GaugeValueArc, useGaugeState } from '@mui/x-charts/Gauge';
import { motion, useReducedMotion } from 'motion/react';
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox';
import { HERO_MOTION } from './motion';
import StatusDot from './StatusDot';
interface HeroGaugeProps {
  heroState: HeroState;
  arcProgress: number;
  displayProgress: number | null;
  verdict: string | null;
  description?: string;
  editSlot?: React.ReactNode;
}
const GAUGE_HEIGHT = 200;
const GaugeArea = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  maxWidth: '28rem'
}));
const Glow = styled(motion.div)(({
  theme
}) => ({
  position: 'absolute',
  inset: `${theme.sizes.spacing['11']} ${theme.sizes.spacing['11']} ${theme.sizes.spacing['07']}`,
  borderRadius: theme.sizes.radius.full,
  backgroundColor: theme.colors.brand.blaze[100],
  filter: 'blur(2rem)',
  pointerEvents: 'none'
}));
const ValueArc = styled(GaugeValueArc)(({
  theme
}) => ({
  fill: theme.colors.text['brand-dark']
}));
const DriftingDashes: React.FC<{
  reduceMotion: boolean;
}> = t0 => {
  const $ = _c(19);
  const {
    reduceMotion
  } = t0;
  const theme = useTheme();
  const {
    cx,
    cy,
    outerRadius,
    innerRadius
  } = useGaugeState();
  const radius = (outerRadius + innerRadius) / 2;
  const {
    dashDrift,
    startDot
  } = HERO_MOTION.loops;
  const t1 = `M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`;
  let t2;
  if ($[0] !== reduceMotion) {
    t2 = reduceMotion ? undefined : {
      strokeDashoffset: [0, dashDrift.offset]
    };
    $[0] = reduceMotion;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      duration: dashDrift.duration,
      repeat: Infinity,
      ease: "linear"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== theme.colors.brand.sand[500]) {
    t4 = <motion.path d={t1} fill="none" stroke={theme.colors.brand.sand[500]} strokeWidth={3} strokeLinecap="round" strokeDasharray="3 25" animate={t2} transition={t3} />;
    $[3] = t1;
    $[4] = t2;
    $[5] = theme.colors.brand.sand[500];
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = cx - radius;
  let t6;
  if ($[7] !== reduceMotion) {
    t6 = reduceMotion ? undefined : {
      scale: [...startDot.scale],
      opacity: [0.65, 1, 0.65]
    };
    $[7] = reduceMotion;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      duration: startDot.duration,
      repeat: Infinity,
      ease: "easeInOut"
    };
    t8 = {
      transformBox: "fill-box",
      transformOrigin: "center"
    };
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] !== cy || $[12] !== t5 || $[13] !== t6 || $[14] !== theme.colors.brand.sand[500]) {
    t9 = <motion.circle cx={t5} cy={cy} r={5} fill={theme.colors.brand.sand[500]} animate={t6} transition={t7} style={t8} />;
    $[11] = cy;
    $[12] = t5;
    $[13] = t6;
    $[14] = theme.colors.brand.sand[500];
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t4 || $[17] !== t9) {
    t10 = <>{t4}{t9}</>;
    $[16] = t4;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
};
const StateText: React.FC<{
  heroState: HeroState;
  verdict: string | null;
}> = t0 => {
  const $ = _c(7);
  const {
    heroState,
    verdict
  } = t0;
  const t1 = verdict ?? "";
  let t2;
  if ($[0] !== t1) {
    t2 = {
      loading: "Loading your data\u2026",
      "no-connections": "No data yet",
      "no-data": "Waiting for first data",
      normal: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const text = t2[heroState];
  let t3;
  if ($[2] !== heroState) {
    t3 = theme => ({
      color: heroState === "no-connections" ? theme.colors.text.secondary : theme.colors.text.tertiary
    });
    $[2] = heroState;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3 || $[5] !== text) {
    t4 = <Typography variant="body-sm" sx={t3}>{text}</Typography>;
    $[4] = t3;
    $[5] = text;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
const HeroGauge: React.FC<HeroGaugeProps> = ({
  heroState,
  arcProgress,
  displayProgress,
  verdict,
  description,
  editSlot
}) => {
  const reduceMotion = !!useReducedMotion();
  const isEmpty = heroState !== 'normal';
  const isWaitingForConnections = heroState === 'no-connections';
  const {
    glow,
    readyChip
  } = HERO_MOTION.loops;
  return <FlexColumn sx={theme => ({
    alignItems: 'center',
    width: '100%',
    maxWidth: '28rem',
    [theme.breakpoints.down('lg')]: {
      marginInline: 'auto'
    }
  })}>
      <GaugeArea>
        {isWaitingForConnections && <Glow aria-hidden initial={{
        opacity: glow.opacity[0]
      }} animate={reduceMotion ? undefined : {
        opacity: [...glow.opacity]
      }} transition={{
        duration: glow.duration,
        repeat: Infinity,
        ease: 'easeInOut'
      }} />}
        <GaugeContainer value={isEmpty ? 0 : Math.max(0, Math.min(100, arcProgress))} valueMin={0} valueMax={100} startAngle={-90} endAngle={90} innerRadius='92%' outerRadius='100%' cornerRadius='50%' height={GAUGE_HEIGHT} margin={0}>
          <GaugeReferenceArc />
          {!isEmpty && <ValueArc skipAnimation />}
          {isWaitingForConnections && <DriftingDashes reduceMotion={reduceMotion} />}
        </GaugeContainer>
      </GaugeArea>
      <FlexColumn sx={theme_0 => ({
      alignItems: 'center',
      gap: theme_0.sizes.spacing['03'],
      marginTop: `-${theme_0.sizes.spacing['13']}`
    })}>
        <Typography variant='heading-4xl' component='div' sx={theme_1 => ({
        color: heroState === 'loading' ? theme_1.colors.text.light : theme_1.colors.text.primary
      })}>
          {heroState === 'normal' && displayProgress !== null ? `${Math.round(displayProgress)}%` : '–'}
        </Typography>
        <FlexRow sx={theme_2 => ({
        alignItems: 'center',
        gap: theme_2.sizes.spacing['04']
      })}>
          {heroState === 'loading' && <StatusDot tone='waiting' pulsing />}
          <StateText heroState={heroState} verdict={verdict} />
          {heroState === 'normal' && editSlot}
        </FlexRow>
        {heroState === 'normal' && description && <Typography variant='body-xs' sx={theme_3 => ({
        color: theme_3.colors.text.tertiary,
        textAlign: 'center',
        maxWidth: '20rem'
      })}>
            {description}
          </Typography>}
        {(heroState === 'no-connections' || heroState === 'no-data') && <Typography variant='body-xs' sx={theme_4 => ({
        color: theme_4.colors.text.tertiary
      })}>
            {isWaitingForConnections ? 'Your goal progress appears here once connected' : 'Your goal progress appears here once data lands'}
          </Typography>}
        {isWaitingForConnections && <motion.div animate={reduceMotion ? undefined : {
        scale: [...readyChip.scale]
      }} transition={{
        duration: readyChip.duration,
        repeat: Infinity,
        ease: 'easeInOut'
      }}>
            <Chip size='small' color='success' variant='outlined' label='Ready to come alive' />
          </motion.div>}
      </FlexColumn>
    </FlexColumn>;
};
export default HeroGauge;